import React, { useState, useEffect, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { storage } from '../Firebase';
import toast from 'react-hot-toast';
import { Button, Table, Spinner } from 'react-bootstrap';
import moment from 'moment-timezone';

const PageAssets = ({ pageID }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const fetchFiles = useCallback(async () => {
    setLoading(true);
    try {
      const storageRef = storage.ref();
      const listRef = storageRef.child(`pages/${pageID}/assets`);
      const res = await listRef.listAll();
      
      const filePromises = res.items.map(async (itemRef) => {
        const url = await itemRef.getDownloadURL();
        const metadata = await itemRef.getMetadata();
        return {
          name: itemRef.name,
          fullPath: itemRef.fullPath,
          url,
          size: metadata.size,
          timeCreated: metadata.timeCreated,
          contentType: metadata.contentType,
        };
      });

      const fileList = await Promise.all(filePromises);
      // Sort by creation time, newest first
      fileList.sort((a, b) => new Date(b.timeCreated) - new Date(a.timeCreated));
      setFiles(fileList);
    } catch (error) {
      console.error('Error fetching files:', error);
      // Don't show error if it's just that the directory doesn't exist yet
      if (error.code !== 'storage/object-not-found') {
        toast.error('Error listing assets.');
      }
    } finally {
      setLoading(false);
    }
  }, [pageID]);

  useEffect(() => {
    if (pageID) {
      fetchFiles();
    }
  }, [pageID, fetchFiles]);

  const onDrop = async (acceptedFiles) => {
    setUploading(true);
    const storageRef = storage.ref();
    const assetsRef = storageRef.child(`pages/${pageID}/assets`);

    try {
      for (const file of acceptedFiles) {
        const timestamp = moment().tz('America/Los_Angeles').format('YYYYMMDD_HHmmss');
        // Clean filename to remove spaces and special chars
        const cleanName = file.name.replace(/[^a-zA-Z0-9.]/g, '_');
        const filename = `${timestamp}_${cleanName}`;
        const fileRef = assetsRef.child(filename);
        
        await fileRef.put(file);
      }
      toast.success('Files uploaded successfully');
      fetchFiles();
    } catch (error) {
      console.error('Error uploading files:', error);
      toast.error('Error uploading files: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({ onDrop });

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      toast.success('URL copied to clipboard!');
    }, (err) => {
      toast.error('Could not copy text: ', err);
    });
  };

  const handleDelete = async (fullPath) => {
    if (!window.confirm('Are you sure you want to delete this file? This cannot be undone.')) {
      return;
    }

    try {
      const fileRef = storage.ref().child(fullPath);
      await fileRef.delete();
      toast.success('File deleted successfully');
      fetchFiles();
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error('Error deleting file: ' + error.message);
    }
  };

  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  };

  return (
    <div className="page-assets">
      <div 
        {...getRootProps()} 
        className={`dropzone p-4 mb-4 border rounded text-center ${isDragActive ? 'bg-light border-primary' : 'bg-white'}`}
        style={{ borderStyle: 'dashed', cursor: 'pointer' }}
      >
        <input {...getInputProps()} />
        {uploading ? (
          <Spinner animation="border" role="status" variant="primary">
            <span className="visually-hidden">Uploading...</span>
          </Spinner>
        ) : (
          <div>
            <i className="fas fa-cloud-upload-alt fa-2x mb-2 text-muted"></i>
            <p className="mb-0">Drag & drop files here, or click to select files</p>
            <small className="text-muted">Images, PDFs, etc.</small>
          </div>
        )}
      </div>

      {loading ? (
        <div className="text-center py-4">
          <Spinner animation="border" size="sm" />
        </div>
      ) : files.length > 0 ? (
        <div className="table-responsive">
          <Table striped bordered hover size="sm">
            <thead>
              <tr>
                <th>Preview</th>
                <th>Name</th>
                <th>Size</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {files.map((file) => (
                <tr key={file.fullPath}>
                  <td style={{ width: '60px', textAlign: 'center' }}>
                    {file.contentType && file.contentType.startsWith('image/') ? (
                      <img 
                        src={file.url} 
                        alt={file.name} 
                        style={{ width: '40px', height: '40px', objectFit: 'cover' }} 
                      />
                    ) : (
                      <i className="fas fa-file fa-lg text-muted"></i>
                    )}
                  </td>
                  <td className="align-middle text-break" style={{ maxWidth: '200px' }}>
                    {file.name}
                  </td>
                  <td className="align-middle">{formatBytes(file.size)}</td>
                  <td className="align-middle">
                    <Button 
                      variant="outline-primary" 
                      size="sm" 
                      className="me-2 mb-1"
                      onClick={() => copyToClipboard(file.url)}
                      title="Copy URL"
                    >
                      <i className="fas fa-link"></i>
                    </Button>
                    <Button 
                      variant="outline-danger" 
                      size="sm"
                      className="mb-1"
                      onClick={() => handleDelete(file.fullPath)}
                      title="Delete"
                    >
                      <i className="fas fa-trash"></i>
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      ) : (
        <div className="text-center text-muted py-3 border rounded bg-light">
          No assets uploaded yet.
        </div>
      )}
    </div>
  );
};

export default PageAssets;
